import { getOrganizationContext } from "@/api";
import { organizationQueryKeys } from "@/api/organizations";
import { OrganizationProvider } from "@/contexts/frontend/organization/OrganizationProvider";
import { usePostHog } from "@posthog/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { DialogsProvider } from "@toolpad/core/useDialogs";
import { formatDate } from "date-fns";
import { useEffect } from "react";
import { Navigate, Outlet } from "react-router";
import { useAuth } from "../../contexts/auth/useAuth";
import HelpScoutBeacon from "../frontend/HelpScoutBeacon";

const ProtectedOrganizationRoute = () => {
    const { user } = useAuth();
    const posthog = usePostHog();

    const { data: organizationContext } = useSuspenseQuery({
        queryKey: organizationQueryKeys.context,
        queryFn: getOrganizationContext,
    });

    const organization = organizationContext.organization;

    useEffect(() => {
        if (!user || !organization) return;

        posthog.group("organization", organization.id.toString(), {
            name: organization.name,
            created_at: organization.created_at,
        });

        // Create people profile for user
        posthog.identify(String(user.id), {
            email: user.email,
            name: `${user.first_name} ${user.last_name}`,
            account_type: user.is_admin ? "admin" : "user",
            last_seen: formatDate(new Date(), "PPpp"),
        });
    }, [user, organization, posthog]);

    if (!organizationContext.has_organization_context) {
        // If the user does not have an organization, redirect to the create organization page
        if (user?.is_admin) {
            return <Navigate to="/admin" replace />;
        } else {
            throw new Error("User does not belong to any organization");
        }
    }

    if (!organizationContext.organization) {
        // This should not happen, but if it does, we throw an error
        throw new Error("User organization not found");
    }

    if (!organizationContext.organization_settings) {
        // This should not happen, but if it does, we throw an error
        throw new Error("Organization settings not found");
    }

    return (
        <OrganizationProvider
            organization={organizationContext.organization}
            settings={organizationContext.organization_settings}
            chatMaxDirectDocuments={
                organizationContext.chat_max_direct_documents
            }
        >
            <HelpScoutBeacon />

            <DialogsProvider>
                <Outlet />
            </DialogsProvider>
        </OrganizationProvider>
    );
};

export default ProtectedOrganizationRoute;
